export type MultiStepCompleteEvent = {
	source: 'view-all' | 'submit'
	values: Record<string, string | string[]>
}

export default function init() {
	[...document.querySelectorAll<HTMLFormElement>('[data-multi-step-form]')].map(multiStepForm)
}

function multiStepForm(form: HTMLFormElement) {

	const steps = [...form.querySelectorAll<HTMLElement>('[data-step]')]
	let current = 0

	const nextBtns = [...form.querySelectorAll<HTMLElement>('[data-step-next]')]
	const backBtns = [...form.querySelectorAll<HTMLElement>('[data-step-back]')]
	const completeBtns = [...form.querySelectorAll<HTMLElement>('[data-step-complete]')]
	const restartBtns = [...form.querySelectorAll<HTMLElement>('[data-step-restart]')]
	const selectBtns = [...form.querySelectorAll<HTMLElement>('[data-step-select]')]

	function show(index: number, scroll: boolean = false) {
		const active = steps[current]
		const next = steps[index]
		if (active === next) return
		const forward = current < index

        // setup form height to current slide
        form.transition = 'none'
        form.getBoundingClientRect()
        form.style.height = `${active.clientHeight}px`
        form.getBoundingClientRect()

        // move next slide to start of animate-in position
        next.style.position = 'absolute'
        next.style.transform = `translateX(${forward ? 100 : -100}%)`
        next.style.display = 'block'
        next.getBoundingClientRect()

        // allow form height animation
        form.transition = ''
        form.getBoundingClientRect()
        
        // animate
        active.style.transform = `translateX(${forward ? -100 : 100}%)`
        active.getBoundingClientRect()

        next.style.transform = `translateX(0)`
        form.style.height = `${next.clientHeight}px`

        // cleanup
        active.addEventListener('transitionend', event => {
            form.style.transition = ''
            form.style.height = ''

            active.classList.remove('is-active')
            active.style.transform = ''
            
            next.classList.add('is-active')
            next.style.position = ''
            next.style.transform = ''
            next.style.display = ''
            
            current = index
        }, { once: true })
        
		if (scroll) form.scrollIntoView({ behavior: 'smooth' })
	}

	function complete(source: MultiStepCompleteEvent['source']) {
		show(steps.length - 1, true)
		form.dispatchEvent(new CustomEvent<MultiStepCompleteEvent>('multistep:complete', {
			detail: {
				source,
				values: collect(form)
			}
		}))
	}

	function reset() {
		form.reset()
		steps.map(step => updateCheckboxes(step))
	}

	form.addEventListener('change', event => {
		const target = event.target as HTMLElement
		if (target.matches('input[type="checkbox"]')) {
			const step = target.closest<HTMLElement>('[data-step]')
			if (step) updateCheckboxes(step)
		}
	})

	nextBtns.map(btn => btn.addEventListener('click', () => {
		if (current < steps.length - 1) show(current + 1, true)
	}))

	backBtns.map(btn => btn.addEventListener('click', () => {
		if (current > 0) show(current - 1, true)
	}))

	completeBtns.map(btn => btn.addEventListener('click', () => complete('view-all')))
	restartBtns.map(btn => btn.addEventListener('click', () => { reset(); show(0, true) }))
	selectBtns.map(btn => btn.addEventListener('click', () => complete('submit')))

	form.addEventListener('submit', event => {
		event.preventDefault()
		complete('submit')
	})

	reset()
	steps[0].classList.add('is-active')
}

function updateCheckboxes(step: HTMLElement) {
	const next = step.querySelector<HTMLButtonElement>('[data-step-next]')
	if (!next) return
	const required = parseInt(next.dataset.required ?? '0', 10)
	const boxes = [...step.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')]
	const checked = step.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').length
	next.disabled = checked < required

	// block extra selections once the required number is selected
	boxes.map(box => {
		if (box.checked) return
		box.disabled = required > 0 && checked >= required
	})

	const count = step.querySelector<HTMLElement>('[data-step-count]')
	if (count) count.textContent = `Selected ${checked} of ${required}`
}

function collect(form: HTMLFormElement): Record<string, string | string[]> {
	const data: Record<string, string | string[]> = {}
	const formData = new FormData(form)
	for (const key of new Set(formData.keys())) {
		const values = formData.getAll(key).map(String)
		data[key] = values.length === 1 ? values[0] : values
	}
	return data
}
